import Script from "next/script";
import { memo, useEffect, useState } from "react";
import { useRouter } from "next/router";

import {
  CLARITY_ID,
  FB_PIXEL_ID,
  GA_MEASUREMENT_ID,
  GTM_ID,
} from "@/lib/config";
import { useAppSelector } from "@/store";

/**
 * Google Tag Manager, Microsoft Clarity and the Facebook Pixel — recovered from
 * the original's provider tree (`_app` bundle module 76201, symbols `X`/`Q` for
 * Clarity, `J`/`ee` for the Pixel, and the GTM element in `A$`).
 *
 * Three properties of the original are reproduced deliberately:
 *
 * 1. **Injection point.** All three mount inside the provider tree, above the
 *    theme, so they load once per session rather than per route.
 * 2. **Load strategy.** `afterInteractive` for both inline scripts, and each is
 *    armed from an effect rather than rendered directly — the original gates on
 *    `useState` flipped in a `useEffect`, so nothing is injected during SSR.
 * 3. **Staff suppression.** Every one of them is skipped when `auth.isStaff` is
 *    true. Staff use this storefront to take orders; counting that as customer
 *    traffic would corrupt the funnel. GTM is additionally suppressed inline,
 *    exactly as the original does.
 *
 * The ids come from the environment (see `.env.example`) and each script is a
 * no-op while its variable is empty. The original's own hardcoded Clarity id is
 * NOT reused — it belongs to the reference tenant.
 */

/** Mirrors the original: arm only on the client, and never for staff. */
function useArmed(id: string, isStaff: boolean) {
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    if (id && !isStaff) setArmed(true);
  }, [id, isStaff]);
  return armed;
}

interface TagProps {
  isStaff: boolean;
  googleTagId?: string;
  analyticsDisabled?: boolean;
}

const GoogleTagManager = memo(function GoogleTagManager({ isStaff, googleTagId, analyticsDisabled }: TagProps) {
  const id = googleTagId?.startsWith("GTM-") ? googleTagId : GTM_ID;
  if (analyticsDisabled || !id || isStaff) return null;
  return (
    <Script
      id="gtm-script"
      strategy="afterInteractive"
      dangerouslySetInnerHTML={{
        __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','${id}');`,
      }}
    />
  );
});

/** Google Analytics 4 for tenant builds that provide a G- measurement id. */
const GoogleAnalytics = memo(function GoogleAnalytics({ isStaff, googleTagId, analyticsDisabled }: TagProps) {
  const router = useRouter();
  const id = googleTagId?.startsWith("G-") ? googleTagId : GA_MEASUREMENT_ID;
  useEffect(() => {
    if (analyticsDisabled || !id || isStaff) return undefined;
    const pageView = (url: string) => {
      const gtag = (window as typeof window & { gtag?: (...args: unknown[]) => void }).gtag;
      gtag?.("event", "page_view", { page_location: `${window.location.origin}${url}` });
    };
    router.events.on("routeChangeComplete", pageView);
    return () => router.events.off("routeChangeComplete", pageView);
  }, [analyticsDisabled, id, isStaff, router.events]);
  if (analyticsDisabled || !id || isStaff) return null;
  return (
    <>
      <Script
        id="ga4-library"
        src={`https://www.googletagmanager.com/gtag/js?id=${id}`}
        strategy="afterInteractive"
      />
      <Script
        id="ga4-config"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}window.gtag=gtag;gtag('js',new Date());gtag('config','${id}',{send_page_view:true});`,
        }}
      />
    </>
  );
});

const Clarity = memo(function Clarity({ isStaff }: TagProps) {
  const armed = useArmed(CLARITY_ID, isStaff);
  if (!armed) return null;
  return (
    <Script
      type="text/javascript"
      strategy="afterInteractive"
      id="clarity_script"
      dangerouslySetInnerHTML={{
        __html: `(function(c,l,a,r,i,t,y){
          c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
          t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
          y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
        })(window, document, "clarity", "script", "${CLARITY_ID}");`,
      }}
    />
  );
});

interface PixelProps extends TagProps {
  email?: string;
  phone?: string;
}

const FacebookPixel = memo(function FacebookPixel({
  isStaff,
  email,
  phone,
}: PixelProps) {
  const armed = useArmed(FB_PIXEL_ID, isStaff);
  if (!armed) return null;
  return (
    <>
      <Script
        id="facebook-pixel"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
            !function(f,b,e,v,n,t,s)
            {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
            n.callMethod.apply(n,arguments):n.queue.push(arguments)};
            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
            n.queue=[];t=b.createElement(e);t.async=!0;
            t.src=v;s=b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t,s)}(window,document,'script',
            'https://connect.facebook.net/en_US/fbevents.js');
            fbq('init', '${FB_PIXEL_ID}', {
              em: '${email || ""}',
              ph: '${phone || ""}'
            });
            fbq('track', 'PageView');
          `,
        }}
      />
      <noscript>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          height="1"
          width="1"
          style={{ display: "none" }}
          src={`https://www.facebook.com/tr?id=${FB_PIXEL_ID}&ev=PageView&noscript=1`}
          alt=""
        />
      </noscript>
    </>
  );
});

export default function Analytics() {
  const isStaff = useAppSelector((state) => state.auth.isStaff);
  const { support_email: supportEmail, public_phone_numbers: phones } =
    useAppSelector((state) => state.settings.data);
  const { gtm_id: tenantGoogleTagId, google_analytics_enabled: analyticsEnabled } =
    useAppSelector((state) => state.settings.data);
  const analyticsDisabled = analyticsEnabled === false;
  const googleTagId = tenantGoogleTagId?.trim();

  return (
    <>
      <GoogleTagManager isStaff={isStaff} googleTagId={googleTagId} analyticsDisabled={analyticsDisabled} />
      <GoogleAnalytics isStaff={isStaff} googleTagId={googleTagId} analyticsDisabled={analyticsDisabled} />
      <Clarity isStaff={isStaff} />
      <FacebookPixel
        isStaff={isStaff}
        email={supportEmail ?? undefined}
        phone={phones?.[0]}
      />
    </>
  );
}
